// src/components/common/Toast.js
import React, { useEffect } from 'react';

const Toast = ({ show, message, type = 'success', duration = 5000, onClose }) => {
    useEffect(() => {
        if (show && duration > 0) {
            const timer = setTimeout(() => {
                onClose();
            }, duration);

            return () => clearTimeout(timer);
        }
    }, [show, duration, onClose]);

    if (!show) return null;

    const getToastIcon = () => {
        switch (type) {
            case 'success':
                return 'fas fa-check-circle';
            case 'error':
                return 'fas fa-exclamation-circle';
            case 'warning':
                return 'fas fa-exclamation-triangle';
            case 'info':
                return 'fas fa-info-circle';
            default:
                return 'fas fa-info-circle';
        }
    };

    return (
        <>
            <div className={`toast toast-${type} ${show ? 'toast-show' : ''}`}>
                <div className="toast-content">
                    <div className="toast-icon">
                        <i className={getToastIcon()}></i>
                    </div>
                    <div className="toast-text">
                        <span className="toast-message">{message}</span>
                    </div>
                    <button className="toast-close" onClick={onClose} aria-label="Close notification">
                        <i className="fas fa-times"></i>
                    </button>
                </div>
                <div className="toast-progress">
                    <div className="toast-progress-bar"></div>
                </div>
            </div>

            <style jsx>{`
                .toast {
                    position: fixed;
                    top: 24px;
                    right: 24px;
                    min-width: 350px;
                    max-width: 450px;
                    background: #ffffff;
                    border-radius: 12px;
                    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
                    z-index: 999999;
                    transform: translateX(120%);
                    transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
                    opacity: 0;
                    border: 1px solid rgba(0, 0, 0, 0.08);
                    overflow: hidden;
                    backdrop-filter: blur(8px);
                }

                .toast-show {
                    transform: translateX(0);
                    opacity: 1;
                }

                .toast-success {
                    border-left: 4px solid #10b981;
                }

                .toast-error {
                    border-left: 4px solid #ef4444;
                }

                .toast-warning {
                    border-left: 4px solid #f59e0b;
                }

                .toast-info {
                    border-left: 4px solid #3b82f6;
                }

                .toast-content {
                    display: flex;
                    align-items: flex-start;
                    padding: 16px 20px;
                    gap: 12px;
                    position: relative;
                }

                .toast-icon {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 24px;
                    height: 24px;
                    border-radius: 50%;
                    flex-shrink: 0;
                    margin-top: 2px;
                }

                .toast-success .toast-icon {
                    background-color: rgba(16, 185, 129, 0.1);
                    color: #10b981;
                }

                .toast-error .toast-icon {
                    background-color: rgba(239, 68, 68, 0.1);
                    color: #ef4444;
                }

                .toast-warning .toast-icon {
                    background-color: rgba(245, 158, 11, 0.1);
                    color: #f59e0b;
                }

                .toast-info .toast-icon {
                    background-color: rgba(59, 130, 246, 0.1);
                    color: #3b82f6;
                }

                .toast-icon i {
                    font-size: 14px;
                    font-weight: 600;
                }

                .toast-text {
                    flex: 1;
                    padding-top: 1px;
                }

                .toast-message {
                    color: #1f2937;
                    font-size: 14px;
                    font-weight: 500;
                    line-height: 1.5;
                    letter-spacing: -0.025em;
                }

                .toast-close {
                    background: none;
                    border: none;
                    cursor: pointer;
                    padding: 4px;
                    color: #9ca3af;
                    transition: all 0.2s ease;
                    flex-shrink: 0;
                    border-radius: 6px;
                    width: 24px;
                    height: 24px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin-top: 2px;
                }

                .toast-close:hover {
                    background-color: rgba(0, 0, 0, 0.05);
                    color: #374151;
                    transform: scale(1.1);
                }

                .toast-close:active {
                    transform: scale(0.95);
                }

                .toast-close i {
                    font-size: 12px;
                    font-weight: 600;
                }

                .toast-progress {
                    position: absolute;
                    bottom: 0;
                    left: 0;
                    right: 0;
                    height: 3px;
                    background-color: rgba(0, 0, 0, 0.05);
                    overflow: hidden;
                }

                .toast-progress-bar {
                    height: 100%;
                    width: 100%;
                    transform: translateX(-100%);
                    transition: transform ${duration}ms linear;
                }

                .toast-show .toast-progress-bar {
                    transform: translateX(0);
                }

                .toast-success .toast-progress-bar {
                    background: linear-gradient(90deg, #10b981, #34d399);
                }

                .toast-error .toast-progress-bar {
                    background: linear-gradient(90deg, #ef4444, #f87171);
                }

                .toast-warning .toast-progress-bar {
                    background: linear-gradient(90deg, #f59e0b, #fbbf24);
                }

                .toast-info .toast-progress-bar {
                    background: linear-gradient(90deg, #3b82f6, #60a5fa);
                }

                /* Hover effect for entire toast */
                .toast:hover {
                    transform: translateX(-4px) scale(1.02);
                    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
                }

                .toast:hover .toast-progress-bar {
                    animation-play-state: paused;
                }

                /* Focus styles for accessibility */
                .toast-close:focus {
                    outline: 2px solid #3b82f6;
                    outline-offset: 2px;
                }

                /* Mobile responsiveness */
                @media (max-width: 768px) {
                    .toast {
                        top: 16px;
                        right: 16px;
                        left: 16px;
                        min-width: auto;
                        max-width: none;
                        border-radius: 8px;
                    }
                    
                    .toast-content {
                        padding: 14px 16px;
                        gap: 10px;
                    }
                    
                    .toast-message {
                        font-size: 13px;
                    }
                    
                    .toast:hover {
                        transform: none;
                        scale: 1;
                    }
                }

                /* High contrast mode support */
                @media (prefers-contrast: high) {
                    .toast {
                        border: 2px solid;
                        box-shadow: none;
                    }
                    
                    .toast-success {
                        border-color: #10b981;
                    }
                    
                    .toast-error {
                        border-color: #ef4444;
                    }
                    
                    .toast-warning {
                        border-color: #f59e0b;
                    }
                    
                    .toast-info {
                        border-color: #3b82f6;
                    }
                }

                /* Reduced motion preferences */
                @media (prefers-reduced-motion: reduce) {
                    .toast {
                        transition: opacity 0.3s ease;
                        transform: none;
                    }
                    
                    .toast-show {
                        transform: none;
                    }
                    
                    .toast:hover {
                        transform: none;
                        scale: 1;
                    }
                    
                    .toast-close:hover {
                        transform: none;
                    }
                    
                    .toast-close:active {
                        transform: none;
                    }
                    
                    .toast-progress-bar {
                        transition: none;
                    }
                }

                /* Dark mode support */
                @media (prefers-color-scheme: dark) {
                    .toast {
                        background: #1f2937;
                        border-color: rgba(255, 255, 255, 0.1);
                        color: #f9fafb;
                    }
                    
                    .toast-message {
                        color: #f9fafb;
                    }
                    
                    .toast-close {
                        color: #9ca3af;
                    }
                    
                    .toast-close:hover {
                        background-color: rgba(255, 255, 255, 0.1);
                        color: #d1d5db;
                    }
                    
                    .toast-progress {
                        background-color: rgba(255, 255, 255, 0.1);
                    }
                }

                /* Print styles */
                @media print {
                    .toast {
                        display: none;
                    }
                }
            `}</style>
        </>
    );
};

export default Toast;